import { START_DATE, SUPPORTED_LANGS, FALLBACK_LANG, TRANSLATIONS } from '../constants';
import { SupportedLang, UserStats } from '../types';

export const getCurrentDay = (): number => {
  const genesis = START_DATE.getTime();
  const now = Date.now();
  const diffTime = Math.max(0, now - genesis);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export const getTimeUntilNextMidnight = (): string => {
  const now = new Date();
  const nextMidnight = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1
  ));
  
  const diff = Math.max(0, nextMidnight.getTime() - now.getTime());
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0')
  ].join(':');
};

/**
 * Formats a date string into a localized long-form date based on language.
 */
export const formatLocalizedDate = (dateString: string, lang: string): string => {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(lang, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  } catch (e) {
    return dateString;
  }
};

/**
 * Deterministic Daily Movie Selection
 * forcedDay is assumed to be a 0-based index.
 */
export const getDailyMovieId = (catalog: any[], forcedDay?: number): number => {
  const day = forcedDay !== undefined ? forcedDay : getCurrentDay();
  const entry = catalog[day % catalog.length];
  return typeof entry === 'object' ? entry.id : entry;
};

/**
 * Smart Random Selection for Infinite Mode
 * Tries to avoid repeats from the current session.
 */
export const getInfiniteMovieId = (catalog: any[], playedIds: Set<number>): number | null => {
  const available = catalog.filter(entry => {
    const id = typeof entry === 'object' ? entry.id : entry;
    return !playedIds.has(id);
  });

  if (available.length === 0) return null; // Signal to fetch trending

  const randomIndex = Math.floor(Math.random() * available.length);
  const selected = available[randomIndex];
  return typeof selected === 'object' ? selected.id : selected;
};

/**
 * Data Engine: Updates user statistics in localStorage.
 * key: 'movie-riddle-stats'
 */
export const updateUserStats = (isWin: boolean, attemptNumber: number, isDaily: boolean, isToday: boolean) => {
  const STATS_KEY = 'movie-riddle-stats';
  const rawStats = localStorage.getItem(STATS_KEY);
  
  const stats: UserStats = rawStats ? JSON.parse(rawStats) : {
    played: 0,
    wins: 0,
    currentStreak: 0,
    maxStreak: 0,
    winDistribution: [0, 0, 0, 0, 0, 0]
  };

  stats.played++;

  if (isWin) {
    stats.wins++;
    // attemptNumber is 1-based (1 to 6)
    const index = Math.min(Math.max(0, attemptNumber - 1), 5);
    stats.winDistribution[index]++;

    // Streak logic only applies to the Official Daily challenge for today
    if (isDaily && isToday) {
      stats.currentStreak++;
      if (stats.currentStreak > stats.maxStreak) {
        stats.maxStreak = stats.currentStreak;
      }
    }
  } else {
    // Loss in the current daily challenge resets streak
    if (isDaily && isToday) {
      stats.currentStreak = 0;
    }
  }

  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
};

export const parseUrlState = () => {
  const pathname = window.location.pathname;
  const parts = pathname.split('/').filter(Boolean);
  
  let lang: SupportedLang = FALLBACK_LANG;
  let day = getCurrentDay();

  for (const part of parts) {
    if (SUPPORTED_LANGS.includes(part as SupportedLang)) {
      lang = part as SupportedLang;
      break;
    }
  }

  for (const part of parts) {
    const parsedDay = parseInt(part);
    if (!isNaN(parsedDay)) {
      // If found in URL, we treat it as 1-based and convert to 0-based internal
      day = Math.max(0, parsedDay - 1);
      break;
    }
  }
  
  return { lang, day };
};

/**
 * Overhauled sharing logic for high-virality social proof.
 * Formats results in a Wordle-style grid with localized branding.
 */
export const getResultGrid = (
  guesses: string[], 
  correctTitle: string, 
  dayNumber: number | string, 
  lang: SupportedLang,
  isWon: boolean
): string => {
  const attempts = isWon ? guesses.length : 'X';
  const score = `🎬 ${attempts}/6`;
  
  // Localized Header
  let header = `Movie Riddle #${dayNumber}`;
  if (lang !== 'en' && typeof dayNumber === 'number') {
    const dayLabel = TRANSLATIONS.dayLabel[lang] || 'Day';
    header += ` - ${dayLabel} ${dayNumber}`;
  } else if (typeof dayNumber !== 'number') {
    header = `Movie Riddle - ${dayNumber}`;
  }

  // Emoji Grid Construction
  let grid = '';
  guesses.forEach((guess) => {
    if (guess.toLowerCase() === correctTitle.toLowerCase()) {
      grid += '🟩';
    } else {
      grid += '🟥';
    }
  });
  
  // Fill remaining slots with unused black squares
  for (let i = guesses.length; i < 6; i++) {
    grid += '⬛';
  }

  // Localized Landing Page
  const prefix = lang === 'en' ? '' : `/${lang}`;
  const url = `https://movieriddle.com${prefix}`;

  return `${header}\n${score}\n${grid}\n\n${url}`;
};

export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
};